/**
 * What an event *is* — Feature #664.
 *
 * A **Kassen-Event** (`Real`) is the historical event: registers pick it up by time, orders attach to it, and
 * it feeds the dashboard, the analytics and the fiscal exports. A **virtuelles Event** (`Virtual`) exists only
 * for planning — the shift plan and the employee calendar see it, registers, dashboards and analytics never
 * do. It is licensed per contract (`ContractBase.virtualEventCount`).
 *
 * The numbers are the wire ordinals of the server's `API.Core.Enums.Event.EventKind` and are what actually
 * travels; do not reorder them. `Real = 0` is deliberately the default everywhere, so an event model built by
 * an older bundle (or by a test fixture that never sets the field) keeps meaning what it always meant.
 *
 * **Kind is fixed at creation.** There is no UI to change it, and the server's event gate re-evaluates the
 * kind on every write rather than accepting a requested change.
 */
export enum EventKind {
  /** Kassen-Event — the register-facing event. */
  Real = 0,
  /** Virtuelles Event — planning only; no register, no sales, no fiscal footprint. */
  Virtual = 1
}

/**
 * The German label for each kind, as it appears on every surface that names one.
 *
 * One table rather than a literal per screen: the creation form's picker, the "Virtuell" chip's tooltip and
 * any future listing must agree, and three copies of two words is three chances to drift.
 */
export const EventKindLabels: Readonly<Record<EventKind, string>> = {
  [EventKind.Real]: 'Kassen-Event',
  [EventKind.Virtual]: 'Virtuelles Event'
};

/**
 * The short chip caption used where a *row* has to say what it is in one word — the events list, the shift
 * plan's event column, the employee calendar card. Only the virtual kind is ever chipped: a Kassen-Event is
 * the norm and a chip on every row would say nothing.
 */
export const VIRTUAL_EVENT_BADGE = 'Virtuell';
